import React, { useState, useEffect, useMemo } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import Chart from 'chart.js';
import { DateTime } from 'luxon';
import { DatePicker } from '@material-ui/pickers';
import { fade } from '@material-ui/core/styles/colorManipulator';

import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';

import AcceptPaymentIcon from '@material-ui/icons/AttachMoney';
import RefreshIcon from '@material-ui/icons/Refresh'
import FileIcon from '@material-ui/icons/Description';
import DeleteIcon from '@material-ui/icons/Delete';

import { paper, adminPageTitle } from 'app/styles/core';
import { containerFlex, containerFlexGrow, containerFlexFixed } from 'app/styles/new-layout';
import Loading from 'app/components/loaders/Loading';

import useApp from 'app/context/useApp';
import useApi from 'app/context/useApi';

const renderDateInput = props => (
    <TextField
        variant="outlined"
        fullWidth
        className={props.className}
        onClick={props.onClick}
        id={props.id}
        value={props.value}
        disabled={props.disabled}
        label={props.label}
        helperText={props.helperText}
        {...props.inputProps}
    />
);

const useStyles = makeStyles(theme => ({
    container: {
        ...containerFlex(theme),
        padding: theme.spacing(3)
    },
    content: {
        ...containerFlexGrow(theme),
        position: 'relative',
        paddingRight: theme.spacing(3),

        [theme.breakpoints.down('md')]: {
            paddingRight: '0px',
        }
    },
    nav: {
        ...containerFlexFixed(theme),
    },
    paper: {
        ...paper(theme),
        position: 'relative',
        marginBottom: theme.spacing(3)
    },
    pageTitle: {
        ...adminPageTitle
    },
    analyticsContainer: {
        marginTop: theme.spacing(3)
    },
    analyticsToolbar: {
        display: 'flex',
        alignItems: 'center',
        paddingTop: theme.spacing(2),
        justifyContent: 'space-between'
    },
    canvasContainer: {
        position: 'relative',
        width: '100%',
        marginBottom: theme.spacing(3)
    },
    analyticsSelect: {
        marginLeft: theme.spacing(2)
    },
    rotate: {
        animation: 'spin 1s infinite linear'
    },
    noRotate: {

    },
    paperBalance: {
        position: 'relative',
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        textAlign: 'left',
        whiteSpace: 'nowrap'
    },
    paperBalanceItem: {
        marginRight: theme.spacing(3),
    },
    marginBottom: {
        marginBottom: theme.spacing(3)
    },
    marginBottomSmall: {
        marginBottom: theme.spacing(1)
    },
    stripeAmount: {
        textAlign: 'right',
    },
    chipSuccess: {
        backgroundColor: theme.palette.success.main,
        color: '#ffffff',
    },
    chipError: {
        backgroundColor: theme.palette.error.main,
        color: '#ffffff',
    },
}));

const timeframes = [
    'Last 30 Days',
    'Last 60 Days',
    'Last 90 Days',
    'Last 180 Days',
    'Current Year',
    'Past 5 Years',
    'All Time'
]

const metrics = [
    {
        value: 'invoice_paid_amount',
        label: 'Total Paid Invoices'
    }
]

const initialConfig = {
    type: 'line',
    data: {
        labels: [],
        datasets: [{
            data: [],

            borderWidth: 1
        }]
    },
    options: {
        legend: {
            display: false
        },
        scales: {
            yAxes: [{
                ticks: {
                    beginAtZero: true,
                    stepSize: 1
                }
            }],
            xAxes: [{
                ticks: {
                    maxRotation: 0,
                    autoSkipPadding: 4
                }
            }]
        }
    }
}

let ctx = null;
let analyticsChart = null;

const reportTypes = [
    {
        value: 'connected_account_balance.summary.1',
        label: 'Balance Summary'
    },{
        value: 'connected_account_payouts.summary.1',
        label: 'Payouts Summary'
    },{
        value: 'connected_account_payouts.itemized.3',
        label: 'Itemized Payouts'
    },{
        value: 'connected_account_payout_reconciliation.summary.1',
        label: 'Payout Reconciliation Summary'
    },{
        value: 'connected_account_payout_reconciliation.itemized.3',
        label: 'Payout Reconciliation Itemized'
    },
];

export default function Dashboard(props) {

    const { app } = useApp();

    const stripeActivated = useMemo(() => {
        return (Array.isArray(app.club.meta.payment_methods) && app.club.meta.payment_methods.includes('stripe')) ? true : false;
    }, [app])

    const classes = useStyles();

    const [ stripeBalance, setStripeBalance ] = useState({
        total: 0,
        available: 0
    })
    const [ analytics, setAnalytics ] = useState([]);

    const [ analyticsTimeFrame, setAnalyticsTimeFrame ] = useState('Last 30 Days');
    const [ analyticsMetric, setAnalyticsMetric ] = useState('invoice_paid_amount');

    const [ analyticsLoading, setAnalyticsLoading ] = useState(false);
    const [ balanceLoading, setBalanceLoading ] = useState(false);

    const [ buttonLoading, setButtonLoading ] = useState(false);

    const [ reports, setReports ] = useState([]);
    const [ reportType, setReportType ] = useState(reportTypes[0]['value']);
    const [ reportEndDate, setReportEndDate ] = useState(DateTime.utc().minus({days: 1}).startOf('day'));
    const [ reportStartDate, setReportStartDate ] = useState(DateTime.utc().minus({days: 8}).startOf('day'))
    const [ reportMaxDate, setReportMaxDate ] = useState(null);
    const [ reportMinDate, setReportMinDate ] = useState(null);
    const [ reportsLoading, setReportsLoading ] = useState(false);
    const [ reportListLoading, setReportListLoading ] = useState(false);
    const [ stripeSecret, setStripeSecret ] = useState(null);

    let theme = useTheme();
    const api = useApi();

    const getAnalytics = () => {
        setAnalyticsLoading(true);

        api.get('/admin/analytics').then(res => setAnalytics(res.analytics)).finally(() => setAnalyticsLoading(false));
    }

    const getBalance = () => {
        setBalanceLoading(true);

        api.get('/admin/accounting/stripe/balance')
            .then(res => {
                if (res.balance) {
                    setStripeBalance({
                        total: res.balance.available[0].amount + res.balance.pending[0].amount,
                        available: res.balance.available[0].amount
                    })
                }
            }).finally(() => setBalanceLoading(false));
    }

    const getReports = () => {
        setReportListLoading(true);

        api.get('/admin/accounting/stripe/report')
            .then(res => {
                setReports(res.reports);
                setStripeSecret(res.stripe_secret);
            }).finally(() => setReportListLoading(false));
    }

    useEffect(() => {
        ctx = document.getElementById('analyticsGraph').getContext("2d");
        analyticsChart = new Chart(ctx, initialConfig);

        getAnalytics();
        getBalance();
        getReports();
    }, []);

    useEffect(() => {
        if (stripeActivated) {
            setReportsLoading(true);

            api.get(`/admin/accounting/stripe/report/availability/${reportType}`)
                .then(res => {
                    const newReportEndDate = reportEndDate;
                    const newReportStartDate = reportStartDate;
                   
                    const maxDate = DateTime.fromSeconds(res.availability.data_available_end);
                    const minDate = DateTime.fromSeconds(res.availability.data_available_start);
                    
                    if (newReportEndDate > maxDate) {
                        newReportEndDate = maxDate;
                        newReportStartDate = maxDate.minus({days: 7});
                    }

                    setReportMaxDate(maxDate);
                    setReportMinDate(minDate);
                    setReportEndDate(newReportEndDate);
                    setReportStartDate(newReportStartDate);
                }).finally(() => setReportsLoading(false));
        }
    }, [reportType])

    const handleAnalyticsMetric = event => {
        setAnalyticsMetric(event.target.value)
    }

    const handleAnalyticsTimeFrame = event => {
        setAnalyticsTimeFrame(event.target.value)
    }

    const createAnalyticsGraph = () => {

        const days = [];
        const data = [];
        const today = DateTime.local().startOf('day').plus({days: 1});
        let day = DateTime.local();

        switch (analyticsTimeFrame) {
            case 'Last 30 Days':
                day = day.minus({days: 30});
                break;
            case 'Last 60 Days':
                day = day.minus({days: 60});
                break;
            case 'Last 90 Days':
                day = day.minus({days: 90});
                break;
            case 'Last 180 Days':
                day = day.minus({days: 180});
                break;
            case 'Current Year':
                day = day.startOf('year');
                break;
            case 'Past 5 Years':
                day = day.minus({years: 5});
                break;
        }

        while (day <= today) {
            const date = day.toLocaleString(DateTime.DATE_MED);
            const key = day.toFormat('yyyy-MM-dd');

            days.push(date);
            data.push(analytics[key] ? analytics[key][analyticsMetric] : 0);
            day = day.plus({days: 1});
        }

        let gradient = null;

        if (ctx) {
            gradient = ctx.createLinearGradient(0, 400, 0, 100);
            gradient.addColorStop(1, theme.palette.secondary.main);
            gradient.addColorStop(0, fade(theme.palette.secondary.main, 0.2));
        }

        analyticsChart.data = {
            labels: days,
            datasets: [{
                data: data,
                borderWidth: 1,
                backgroundColor: gradient
            }]
        }

        analyticsChart.update();
    }

    useEffect(() => {

        if (analyticsChart) createAnalyticsGraph();

    }, [analytics, analyticsTimeFrame, analyticsMetric]);

    // Posters -------------------------------------------

    const invoiceMembershipDues = () => {
        setButtonLoading(true);

        api.post('/admin/accounting/invoice/members/dues').finally(() => setButtonLoading(false));
    }

    const applyLateFees = () => {
        setButtonLoading(true);

        api.post('/admin/accounting/invoice/members/late').finally(() => setButtonLoading(false));
    }

    const sendInvoiceEmails = () => {
        setButtonLoading(true);

        api.get('/admin/accounting/invoice/send/email').finally(() => setButtonLoading(false));
    }

    const sendLateNotices = () => {
        setButtonLoading(true);

        api.get('/admin/accounting/invoice/send/notice').finally(() => setButtonLoading(false));
    }

    const generateReport = () => {
        setReportsLoading(true);

        const data = {
            reportType: reportType,
            reportStartDate: reportStartDate.toSeconds(),
            reportEndDate: reportEndDate.toSeconds(),
        }

        api.post(`/admin/accounting/stripe/report/generate`, data)
            .then(getReports)
            .finally(() => setReportsLoading(false));
    }

    const downloadReport = (report) => {

        if (report.status !== 'succeeded') return;

        setReportListLoading(true);

        api.get(`/admin/accounting/stripe/report/${report.id}`, {
            responseType: 'blob',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'text/csv',
            }
        }).then(blob => {
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([blob]));
            const link = document.createElement('a');

            const title = reportTypes.find(rt => rt.value === report.report_type).label;
            const startDate = DateTime.fromSeconds(report.parameters.interval_start);
            const endDate = DateTime.fromSeconds(report.parameters.interval_end);

            link.href = url;
            link.setAttribute(
                'download',
                `${title} - ${startDate.toLocaleString(DateTime.DATE_SHORT)} - ${endDate.toLocaleString(DateTime.DATE_SHORT)}.csv`,
            );
        
            // Append to html link element page
            document.body.appendChild(link);
        
            // Start download
            link.click();
        
            // Clean up and remove the link
            link.parentNode.removeChild(link);
        })
        .finally(() => setReportListLoading(false));
    }

    const deleteReport = (id) => {
        setReportListLoading(true);

        api.delete(`/admin/accounting/stripe/report/${id}`)
            .then(getReports)
            .catch(() => setReportListLoading(false));
    }

    const getStatusChip = s => {
        switch (s) {
            case 'pending': return (<Chip label="Pending"/>);
            case 'succeeded': return (<Chip label="Succeeded" className={classes.chipSuccess}/>);
            case 'failed': return (<Chip label="Declined" className={classes.chipError}/>);
        }
    }

    return (
        <div className={classes.container}>
            <div className={classes.content}>
                <Typography variant="h1" className={classes.pageTitle}>Accounting Dashboard</Typography>
                <div className={classes.analyticsContainer}>
                    <div className={classes.analyticsToolbar}>
                        <Typography variant="h5">Accounting Overview</Typography>
                        <div>
                            <TextField
                                id="select-metric"
                                select
                                label="Select A Metric"
                                value={analyticsMetric}
                                onChange={handleAnalyticsMetric}
                                helperText="Select the metric you wish to view"
                                variant="outlined"
                                className={classes.analyticsSelect}
                                >
                                {metrics.map(metric => (
                                    <MenuItem key={metric.value} value={metric.value}>
                                        {metric.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                id="select-time-frame"
                                select
                                label="Select A Timeframe"
                                value={analyticsTimeFrame}
                                onChange={handleAnalyticsTimeFrame}
                                helperText="Select the timeframe you wish to view"
                                variant="outlined"
                                className={classes.analyticsSelect}
                                >
                                {timeframes.map((t, i) => (
                                    <MenuItem key={i} value={t}>
                                        {t}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <Tooltip title="Refresh Analytics">
                                <IconButton
                                    aria-label="Refresh Analytics"
                                    onClick={getAnalytics}
                                    className={classes.analyticsSelect}
                                    >
                                    <RefreshIcon
                                        className={analyticsLoading ? classes.rotate : classes.noRotate} fontSize="small"
                                    />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </div>
                    <div className={classes.canvasContainer}>
                        <canvas id="analyticsGraph"></canvas>
                    </div>
                    <Paper className={classes.paper}>
                        <Loading isLoading={reportListLoading}/>
                        <Grid container justifyContent='space-between' alignItems='center'>
                            <Grid item>
                                <Typography
                                    variant="h6"
                                    color="textSecondary"
                                    >
                                    Generated Reports
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Refresh Reports">
                                    <IconButton
                                        aria-label="Refresh Reports"
                                        onClick={getReports}
                                        className={classes.analyticsSelect}
                                        >
                                        <RefreshIcon
                                            className={reportListLoading ? classes.rotate : classes.noRotate} fontSize="small"
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                        <List dense>
                            {(reports.length > 0) ? reports.map(report => {
            
                                const title = reportTypes.find(rt => rt.value === report.report_type).label;
                                const startDate = DateTime.fromSeconds(report.parameters.interval_start).setZone('UTC');
                                const endDate = DateTime.fromSeconds(report.parameters.interval_end).setZone('UTC');

                                return (
                                    <ListItem button key={report.id} onClick={() => downloadReport(report)}>
                                        <ListItemIcon>
                                            <FileIcon />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={
                                                <Grid container alignItems="center" spacing={3}>
                                                    <Grid item>{title}</Grid>
                                                    <Grid item>{getStatusChip(report.status)}</Grid>
                                                </Grid>
                                            }
                                            secondary={`${startDate.toLocaleString(DateTime.DATE_SHORT)} - ${endDate.toLocaleString(DateTime.DATE_SHORT)}`}
                                        />
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" aria-label="delete" onClick={() => deleteReport(report.id)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    </ListItem>
                                )
                            }) : (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    >
                                    No generated reports available at this time.
                                </Typography>
                            )}
                        </List>
                    </Paper>
                </div>
            </div>
            <div className={classes.nav}>
                <Paper className={classes.paper}>
                    <Typography
                        variant="h6"
                        color="textSecondary"
                        className={classes.marginBottom}
                        >
                        Your Balance
                    </Typography>
                    {stripeActivated ? (
                        <>
                            <Typography variant="subtitle2" color="textSecondary">
                                <Grid container justifyContent='space-between'>
                                    <Grid item>Total Balance:</Grid>
                                    <Grid item>${(stripeBalance.total / 100).toFixed(2)}</Grid>
                                </Grid>
                            </Typography>
                            <Typography variant="subtitle2" color="primary">
                                <Grid container justifyContent='space-between'>
                                    <Grid item>Available Balance:</Grid>
                                    <Grid item>${(stripeBalance.available / 100).toFixed(2)}</Grid>
                                </Grid>
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="caption" className={classes.marginBottom}>
                                *Online Payments have not been enabled.
                            </Typography>
                            <Tooltip title="Start accepting payments online in a few easy steps." disableFocusListener>
                                <Button
                                    aria-label="Enable Online Payments"
                                    variant="contained"
                                    component={RouterLink}
                                    to={"/admin/accounting/stripe/onboarding"}
                                    startIcon={<AcceptPaymentIcon />}
                                    color="primary"
                                    >
                                    Enable Stripe Payments
                                </Button>
                            </Tooltip>
                        </>
                    )}
                </Paper>
                <Paper className={classes.paper}>
                    <Typography
                        variant="h6"
                        color="textSecondary"
                        className={classes.marginBottom}
                        >
                        Quick Actions
                    </Typography>
                    <Button fullWidth onClick={invoiceMembershipDues} color="primary" variant="contained" disabled={buttonLoading} className={classes.marginBottom}>Invoice Membership Dues</Button>
                    <Button fullWidth onClick={applyLateFees} color="primary" variant="contained" disabled={buttonLoading} className={classes.marginBottom}>Apply Late Fees</Button>
                    <Button fullWidth onClick={sendInvoiceEmails} color="primary" variant="contained" disabled={buttonLoading} className={classes.marginBottom}>Send Invoice Emails</Button>
                    <Button fullWidth onClick={sendLateNotices} color="primary" variant="contained" disabled={buttonLoading}>Send Late Notices</Button>
                </Paper>
                <Paper className={classes.paper}>
                    <Typography
                        variant="h6"
                        color="textSecondary"
                        className={classes.marginBottom}
                        >
                        Generate A Report
                    </Typography>
                    <TextField
                        id="report-type-select"
                        select
                        value={reportType}
                        onChange={e => setReportType(e.target.value)}
                        label="Report Type"
                        helperText="Select the report you would like to generate."
                        variant="outlined"
                        fullWidth
                        color="primary"
                        disabled={reportsLoading || !stripeActivated}
                        className={classes.marginBottom}
                        >
                        {reportTypes.map(rt => (
                            <MenuItem key={rt.value} value={rt.value}>
                                {rt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <DatePicker
                        label="Start Date"
                        inputVariant="outlined"
                        value={reportStartDate}
                        onChange={date => setReportStartDate(date)}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        disabled={reportsLoading || !stripeActivated}
                        className={classes.marginBottomSmall}
                        maxDate={reportMaxDate}
                        minDate={reportMinDate}
                    />
                    <DatePicker
                        label="End Date"
                        inputVariant="outlined"
                        value={reportEndDate}
                        onChange={date => setReportEndDate(date)}
                        variant="outlined"
                        TextFieldComponent={renderDateInput}
                        disabled={reportsLoading || !stripeActivated}
                        maxDate={reportMaxDate}
                        minDate={reportMinDate}
                        className={classes.marginBottom}
                    />
                    <Button 
                        fullWidth
                        onClick={generateReport}
                        color="primary"
                        variant="contained"
                        disabled={reportsLoading || !stripeActivated}
                        >
                        Generate Report
                    </Button>
                </Paper>
            </div>
        </div>
    )
}
